'use client';

import Button from '@/components/ui/Button';
import type { GiftData, GiftDeliveryType } from '@/global/types';
import { COURSE_GIFT_DEDICATION_LIMITS, isCourseGiftDedicationWithinLimits } from '@/utils/course-gift-dedication';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useCart } from 'react-use-cart';
import styles from './HeroVirtual.module.scss';
import type { HeroVirtualTypes } from './HeroVirtual.types';

type Props = {
  course: HeroVirtualTypes['course'];
  onClose: () => void;
};

const deliveryOptions: Array<{
  value: GiftDeliveryType;
  title: string;
  description: string;
}> = [
  {
    value: 'DIGITAL_PDF',
    title: 'Cyfrowy plik PDF',
    description: 'Kupujący otrzyma kod prezentowy w formie pliku PDF.',
  },
  {
    value: 'PHYSICAL_CARD',
    title: 'Fizyczna kartka z wysyłką',
    description: 'System utworzy kod, a kartka zostanie przygotowana i wysłana ręcznie.',
  },
];

const GiftPurchaseModal = ({ course, onClose }: Props) => {
  const { addItem, inCart } = useCart();
  const [deliveryType, setDeliveryType] = useState<GiftDeliveryType>('DIGITAL_PDF');
  const [useDedication, setUseDedication] = useState(false);
  const [dedication, setDedication] = useState({
    from: '',
    to: '',
    message: '',
  });

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const addGiftToCart = () => {
    const normalizedDedication = {
      from: dedication.from.trim(),
      to: dedication.to.trim(),
      message: dedication.message.trim(),
    };
    const hasCompleteDedication = Object.values(normalizedDedication).every(Boolean);

    if (useDedication && !hasCompleteDedication) {
      toast.error('Uzupełnij nadawcę, odbiorcę i wiadomość albo wybierz prezent bez dedykacji');
      return;
    }

    if (useDedication && !isCourseGiftDedicationWithinLimits(normalizedDedication)) {
      toast.error('Dedykacja jest za długa. Skróć ją, żeby zmieściła się w PDF.');
      return;
    }

    const giftData: GiftData = {
      isGift: true,
      deliveryType,
      dedication: useDedication ? normalizedDedication : null,
    };
    const cartItemId = `${course._id}:gift:${deliveryType}`;
    const cartItem = {
      id: cartItemId,
      product: course._id,
      price: 0,
      giftData,
    };

    if (inCart(cartItemId)) {
      toast('Prezent w tej wersji jest już w koszyku');
      onClose();
      return;
    }

    addItem(cartItem, 1);
    toast('Kurs jako prezent został dodany do koszyka');
    onClose();
  };

  return (
    <div
      className={styles['gift-modal-overlay']}
      role='presentation'
      onClick={onClose}
    >
      <div
        className={styles['gift-modal']}
        role='dialog'
        aria-modal='true'
        aria-labelledby='gift-modal-title'
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles['gift-modal-header']}>
          <div>
            <p>Kup jako prezent</p>
            <h2 id='gift-modal-title'>{course.name}</h2>
          </div>
          <button
            type='button'
            className={styles['gift-modal-close']}
            onClick={onClose}
            aria-label='Zamknij okno zakupu prezentu'
          >
            ×
          </button>
        </div>

        <div className={styles['gift-options']}>
          {deliveryOptions.map((option) => (
            <label
              key={option.value}
              className={styles['gift-option']}
              data-active={deliveryType === option.value}
            >
              <input
                type='radio'
                name='giftDeliveryType'
                value={option.value}
                checked={deliveryType === option.value}
                onChange={() => setDeliveryType(option.value)}
              />
              <span>
                <strong>{option.title}</strong>
                <small>{option.description}</small>
              </span>
            </label>
          ))}
        </div>

        <div className={styles['gift-dedication-options']}>
          <label
            className={styles['gift-dedication-option']}
            data-active={!useDedication}
          >
            <input
              type='radio'
              name='giftDedicationMode'
              checked={!useDedication}
              onChange={() => setUseDedication(false)}
            />
            <span>
              <strong>Bez dedykacji</strong>
            </span>
          </label>
          <label
            className={styles['gift-dedication-option']}
            data-active={useDedication}
          >
            <input
              type='radio'
              name='giftDedicationMode'
              checked={useDedication}
              onChange={() => setUseDedication(true)}
            />
            <span>
              <strong>Z dedykacją</strong>
            </span>
          </label>
        </div>

        {useDedication && (
          <fieldset className={styles['gift-dedication']}>
            <legend>Dedykacja</legend>
            <label>
              <span>Od</span>
              <input
                value={dedication.from}
                maxLength={COURSE_GIFT_DEDICATION_LIMITS.from}
                onChange={(event) =>
                  setDedication((prev) => ({
                    ...prev,
                    from: event.target.value.slice(0, COURSE_GIFT_DEDICATION_LIMITS.from),
                  }))
                }
                placeholder='np. Mama'
                required
              />
            </label>
            <label>
              <span>Do</span>
              <input
                value={dedication.to}
                maxLength={COURSE_GIFT_DEDICATION_LIMITS.to}
                onChange={(event) =>
                  setDedication((prev) => ({
                    ...prev,
                    to: event.target.value.slice(0, COURSE_GIFT_DEDICATION_LIMITS.to),
                  }))
                }
                placeholder='np. Kasia'
                required
              />
            </label>
            <label>
              <span>Wiadomość</span>
              <textarea
                value={dedication.message}
                maxLength={COURSE_GIFT_DEDICATION_LIMITS.message}
                onChange={(event) =>
                  setDedication((prev) => ({
                    ...prev,
                    message: event.target.value.slice(0, COURSE_GIFT_DEDICATION_LIMITS.message),
                  }))
                }
                placeholder='Krótka wiadomość dla osoby obdarowanej'
                required
              />
              <small>
                {dedication.message.length}/{COURSE_GIFT_DEDICATION_LIMITS.message} znaków
              </small>
            </label>
          </fieldset>
        )}

        <div className={styles['gift-modal-actions']}>
          <button
            type='button'
            className='link'
            onClick={onClose}
          >
            Anuluj
          </button>
          <Button
            type='button'
            onClick={addGiftToCart}
          >
            Dodaj prezent do koszyka
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GiftPurchaseModal;
